import React, { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { Alert, Button, Divider, Input, Segmented, Space } from 'antd'
import { PortalArticleBlock, PortalPillarConfig } from '../../../models/PortalArtigoModel'
import { getPortalPillarIndexEntries } from '../helpers/anchor-utils'
import { PillarIndexContentModal } from './PillarIndexContentModal'
import { PortalEditorField } from './PortalEditorField'
import { PortalImagePathField } from './PortalImagePathField'
import { RichTextListEditor } from './PortalRichTextFields'

interface PillarConfigEditorProps {
  value?: PortalPillarConfig
  blocks: PortalArticleBlock[]
  onChange: (value: PortalPillarConfig) => void
}

export function PillarConfigEditor({
  value,
  blocks,
  onChange,
}: PillarConfigEditorProps): React.ReactElement {
  const [indexModalOpen, setIndexModalOpen] = useState(false)
  const config = value || {}
  const sidebar = config.sidebar || {}
  const indexCta = sidebar.indexCta || {}
  const banner = sidebar.banner || {}
  const indexEntries = useMemo(() => getPortalPillarIndexEntries(blocks), [blocks])
  const indexVisible = sidebar.showIndex !== false
  const legacyBannerHasContent = Boolean(
    banner.title || banner.label || banner.href || banner.paragraphs?.length
  )
  const bannerVisible = banner.visible !== false && Boolean(banner.type || legacyBannerHasContent)
  const bannerType = banner.type || (legacyBannerHasContent ? 'text' : undefined)

  const updateSidebar = (patch: Partial<NonNullable<PortalPillarConfig['sidebar']>>) => {
    onChange({ ...config, sidebar: { ...sidebar, ...patch } })
  }

  const updateBanner = (patch: Partial<NonNullable<typeof sidebar.banner>>) => {
    updateSidebar({ banner: { ...banner, ...patch } })
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <SectionTitle>Índice lateral</SectionTitle>
      <Button block onClick={() => updateSidebar({ showIndex: !indexVisible })}>
        {indexVisible ? 'Ocultar índice' : 'Exibir índice'}
      </Button>
      {indexVisible && (
        <>
          <PortalEditorField label="Título do índice">
            <Input
              value={sidebar.indexTitle}
              placeholder="Índice"
              onChange={(event) => updateSidebar({ indexTitle: event.target.value })}
            />
          </PortalEditorField>
          <PortalEditorField label="Texto do botão do índice">
            <Input
              value={indexCta.label}
              placeholder="Matricule-se no curso"
              onChange={(event) =>
                updateSidebar({ indexCta: { ...indexCta, label: event.target.value } })
              }
            />
          </PortalEditorField>
          <PortalEditorField label="URL do botão do índice">
            <Input
              value={indexCta.href}
              placeholder="/curso-especializado..."
              onChange={(event) =>
                updateSidebar({ indexCta: { ...indexCta, href: event.target.value } })
              }
            />
          </PortalEditorField>
          <Button
            block
            onClick={() => setIndexModalOpen(true)}
            disabled={indexEntries.length === 0}
          >
            Editar conteúdo
          </Button>
        </>
      )}

      <Divider />
      <SectionTitle>Banner lateral</SectionTitle>
      {bannerVisible && (
        <Button block onClick={() => updateBanner({ visible: false })}>
          Ocultar banner
        </Button>
      )}
      {!bannerVisible && (
        <Alert
          type="warning"
          showIcon={false}
          message="Se nenhum formato de exibição for selecionado, o banner ficará oculto da página."
        />
      )}
      <Segmented
        block
        value={bannerVisible ? bannerType : ''}
        options={[
          { label: 'Texto', value: 'text' },
          { label: 'Imagem', value: 'image' },
        ]}
        onChange={(type) => updateBanner({ visible: true, type: type as 'text' | 'image' })}
      />
      {bannerVisible && (
        <>
          {bannerType === 'image' ? (
            <>
              <PortalImagePathField
                value={{ url: banner.image?.src, alt: banner.image?.alt }}
                showDimensions={false}
                onChange={(image) =>
                  updateBanner({ image: { ...banner.image, src: image.url, alt: image.alt } })
                }
              />
              <PortalEditorField label="URL de destino">
                <Input
                  value={banner.image?.href}
                  placeholder="https://..."
                  onChange={(event) =>
                    updateBanner({ image: { ...banner.image, href: event.target.value } })
                  }
                />
              </PortalEditorField>
            </>
          ) : (
            <>
              <PortalEditorField label="Título do banner">
                <Input
                  value={banner.title}
                  placeholder="Banner"
                  onChange={(event) => updateBanner({ title: event.target.value })}
                />
              </PortalEditorField>
              <RichTextListEditor
                label="Parágrafos do banner"
                addLabel="Adicionar parágrafo"
                value={banner.paragraphs || []}
                onChange={(paragraphs) => updateBanner({ paragraphs })}
              />
              <PortalEditorField label="Texto do botão do banner">
                <Input
                  value={banner.label}
                  placeholder="CTA"
                  onChange={(event) => updateBanner({ label: event.target.value })}
                />
              </PortalEditorField>
              <PortalEditorField label="URL do botão do banner">
                <Input
                  value={banner.href}
                  placeholder="/materiais"
                  onChange={(event) => updateBanner({ href: event.target.value })}
                />
              </PortalEditorField>
            </>
          )}
        </>
      )}

      <PillarIndexContentModal
        open={indexModalOpen}
        entries={indexEntries}
        labels={sidebar.indexLabels}
        onCancel={() => setIndexModalOpen(false)}
        onApply={(indexLabels) => {
          updateSidebar({ indexLabels })
          setIndexModalOpen(false)
        }}
      />
    </Space>
  )
}

const SectionTitle = styled.strong`
  display: block;
  color: #122a41;
`
